import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import LeadRow from './LeadRow';
import LeadsIsEmpty from './LeadsIsEmpty';
import Spinner from 'components/UI/Spinner';
import { useLeadsData, useLeadsActions } from '../LeadsProvider';
import styles from './LeadsTable.module.scss';

interface LeadsTableProps {
    className?: string;
}

export const LeadsTable: FunctionComponent<LeadsTableProps> = ({ className }) => {
    const { leads, isLoading, page, totalPages, size, setPage, setSize, hasActiveFilters, isGlobalEmpty } =
        useLeadsData();
    const { openAddForm } = useLeadsActions();

    const isMobile = useMediaQuery({ query: '(max-width:768px)' });
    const isTabletOrBelow = useMediaQuery({ query: '(max-width:960px)' });

    const colSpan = 8;
    const showHeader = !isMobile || (!isGlobalEmpty && !isLoading);

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        if (leads.length === 0 && hasActiveFilters) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            style={{ padding: '0' }}
                        >
                            <LeadsIsEmpty
                                title="Oops! No result found"
                                text="What you search was unfortunately not found or doesn't exist, try to find something else."
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        if (isGlobalEmpty) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            style={{ padding: '0' }}
                        >
                            <LeadsIsEmpty onClick={openAddForm} />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        return (
            <TableBody>
                {leads.map((lead, index) => (
                    <LeadRow
                        key={lead.id}
                        lead={lead}
                        isLast={leads.length === index + 1 && leads.length !== 1}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            className={[styles.table, className].filter(Boolean).join(' ')}
            fullHeight={true}
            firstColumnFixed={isTabletOrBelow}
            pagination={{ page, totalPages, size }}
            onPageChange={setPage}
            onSizeChange={setSize}
        >
            {showHeader && (
                <TableHeader className={styles.tableHeader}>
                    <TableHeaderCell className={styles.firstHeaderCell}>Name</TableHeaderCell>
                    <TableHeaderCell>Pets</TableHeaderCell>
                    <TableHeaderCell>Contact</TableHeaderCell>
                    <TableHeaderCell>Last Contacted</TableHeaderCell>
                    <TableHeaderCell>Source</TableHeaderCell>
                    <TableHeaderCell>Lead Age</TableHeaderCell>
                    <TableHeaderCell>Stage</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                </TableHeader>
            )}
            {renderTableContent()}
        </Table>
    );
};

export default LeadsTable;
